import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef, forwardRef, ElementRef } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { MatPaginator, MatSort, MatDialog } from "@angular/material";
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { Actions } from '@ngrx/effects';

import { AddUserDialogComponent } from './add-user-dialog.component';

import { ModuleConfiguration } from '../customer.module.config';
import { TileState } from '../../pillar/dashboard/store/state/TileState';
import { AppConfigurationService } from '../../core/services/AppConfigurationService';
import { StoreDataSource } from '../../core/services/StoreDataSource';
import { UserListModel } from '../models/UserListModel';
import { UserModel } from '../models/UserModel';
import * as fromStore from '../store/reducers';
import * as userActions from '../store/actions';
import * as actions from '../../pillar/dashboard/store/actions';
import { SearchComponent } from '../../pillar/search/components/search.component';
import { AlertsComponent } from '../../pillar/alerts/components/alerts.component';
import { TileComponent } from '../../pillar/dashboard/components/tile.component';
import { TileContainer } from '../../pillar/dashboard/services/TileContainer';

@Component({
  selector: 'brd-user-tile',
  providers: [{ provide: TileComponent, useExisting: forwardRef(() => UserTileComponent) }],
  templateUrl: './user-tile.component.html',
  styleUrls: ['./user-tile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserTileComponent extends TileComponent implements OnInit, OnDestroy {
  isDelegatingAccountManagementToOtherSystem: boolean = false;
  isHybridAccountManagement: boolean = false;
  displayedColumns = [];
  dataSource: StoreDataSource<UserListModel>;
  loading$: Observable<boolean>;
  destroyed$: Subject<boolean> = new Subject<boolean>();
  errors$: Observable<string[]>;
  accountActionLoading: Map<string, boolean> = new Map<string, boolean>();
  globalLockStatus$: Observable<boolean>;
  userActions: any;

  viewPermission: string = 'CustomerModule.Users:View';
  lockPermission: string = 'CustomerModule.Users:Lock';
  unlockPermission: string = 'CustomerModule.Users:Unlock';
  disablePermission: string = 'CustomerModule.Users:Disable';
  enablePermission: string = 'CustomerModule.Users:Enable';
  globalLockUnlockPermission: string = 'CustomerModule.Users:GlobalLockUnlock';
  resetPermission: string = 'CustomerModule.Users:ResetPassword';
  editPermission = 'CustomerModule.Users:Edit';

  @ViewChild(MatPaginator)
  paginator: MatPaginator;
  @ViewChild(MatSort)
  sort: MatSort;
  @ViewChild(SearchComponent)
  search: SearchComponent;
  @ViewChild(AlertsComponent)
  alerts: AlertsComponent;

  constructor(private store: Store<TileState<UserListModel, UserModel>>,
              private router: Router,
              private actions$: Actions,
              private dialog: MatDialog,
              private changeDetectorRef: ChangeDetectorRef,
              elementRef: ElementRef,
              private appConfigurationService: AppConfigurationService,
              private tileContainer: TileContainer) {
    super(elementRef);
    tileContainer.tileComponents.push(this);
    this.userActions = userActions;
    this.isDelegatingAccountManagementToOtherSystem = appConfigurationService.configuration.securityServer.isDelegatingAccountManagementToOtherSystem;
    this.isHybridAccountManagement = appConfigurationService.configuration.isHybridAccountManagement;
    this.displayedColumns = this.isDelegatingAccountManagementToOtherSystem && !this.isHybridAccountManagement ? ['identifier', 'displayName', 'status'] : ['identifier', 'displayName', 'status', 'actions'];
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
  }

  ngOnInit() {
    this.errors$ = this.store.select(fromStore.getUserListErrors);
    this.refreshDataSource();
    this.loading$ = this.store.select(fromStore.getUserListLoading);

    this.actions$.ofType(userActions.RESET_PASSWORD_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe((a: userActions.ResetPasswordSuccessAction) => {
        this.accountActionLoading[a.payload.user.key] = false;
        this.alerts.addSuccess(`${a.payload.user.identifier}: Reset Password Success`);
        this.changeDetectorRef.markForCheck();
      });

    this.actions$.ofType(userActions.LOCK_ACCOUNT_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe((a: userActions.LockAccountSuccessAction) => {
        this.accountActionLoading[a.payload.user.key] = false;
        this.alerts.addSuccess(`${a.payload.user.identifier}: Lock Account Success`);
        this.changeDetectorRef.markForCheck();
      });

    this.actions$.ofType(userActions.DISABLE_ACCOUNT_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe((a: userActions.DisableAccountSuccessAction) => {
        this.accountActionLoading[a.payload.user.key] = false;
        this.alerts.addSuccess(`${a.payload.user.identifier}: Disable Account Success`);
        this.changeDetectorRef.markForCheck();
      });

    this.actions$.ofType(userActions.UNLOCK_ACCOUNT_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe((a: userActions.UnlockAccountSuccessAction) => {
        this.accountActionLoading[a.payload.user.key] = false;
        this.alerts.addSuccess(`${a.payload.user.identifier}: Unlock Account Success`);
        this.changeDetectorRef.markForCheck();
      });

    this.actions$.ofType(userActions.ENABLE_ACCOUNT_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe((a: userActions.EnableAccountSuccessAction) => {
        this.accountActionLoading[a.payload.user.key] = false;
        this.alerts.addSuccess(`${a.payload.user.identifier}: Enable Account Success`);
        this.changeDetectorRef.markForCheck();
      });

      this.globalLockStatus$ = this.store.select(fromStore.getGlobalLockStatus);

      this.store.dispatch(new userActions.CheckGlobalLockAction());

      var types = [
        userActions.GLOBAL_LOCK_SUCCESS,
        userActions.GLOBAL_UNLOCK_SUCCESS
      ];
      this.actions$
        .takeUntil(this.destroyed$)
        .filter(a => Boolean(types.find(t => a.type === t)))
        .subscribe(() => {
          this.refreshDataSource();
        });
  }

  private refreshDataSource(){
    this.dataSource = new StoreDataSource<UserListModel>(
      this.destroyed$,
      this.paginator,
      this.store,
      this.store.select(fromStore.getUserList),
      this.store.select(fromStore.getUserListTotalCount),
      this.store.select(fromStore.getUserListLoading),
      (p, s, search) => new actions.QueryListAction(ModuleConfiguration.moduleName,
        UserListModel,
        {
          page: p.pageIndex,
          pageSize: p.pageSize,
          sort: s.active,
          sortDirection: s.direction,
          searchTerm: search.searchControl.value
        }),
      this.sort,
      this.search
    );
  }

  selectItem(item: UserListModel) {
    this.router.navigateByUrl('user-workspace/' + item.key);
  }

  resetPassword(item: UserListModel) {
    this.accountActionLoading[item.key] = true;
    this.store.dispatch(new userActions.ResetPasswordAction({ user: item }));
  }

  lockAccount(item: UserListModel) {
    this.accountActionLoading[item.key] = true;
    this.store.dispatch(new userActions.LockAccountAction({ user: item }));
  }

  unlockAccount(item: UserListModel) {
    this.accountActionLoading[item.key] = true;
    this.store.dispatch(new userActions.UnlockAccountAction({ user: item }));
  }

  disableAccount(item: UserListModel) {
    this.accountActionLoading[item.key] = true;
    this.store.dispatch(new userActions.DisableAccountAction({ user: item }));
  }

  enableAccount(item: UserListModel) {
    this.accountActionLoading[item.key] = true;
    this.store.dispatch(new userActions.EnableAccountAction({ user: item }));
  }

  showAdd(): void {
    this.dialog.open(AddUserDialogComponent, {
      panelClass: 'customized-dialog',
      width: '648px'
    });
  }

  globalLock(): void {
    this.store.dispatch(new userActions.GlobalLockAction());
  }

  globalUnlock(): void {
    this.store.dispatch(new userActions.GlobalUnLockAction());
  }

}



// WEBPACK FOOTER //
// ./src/app/customer/components/user-tile.component.ts